import { logger } from './logger.js';
import { platformBrandsRepo } from './repositories/platform-brands-repo.js';
import { hostnameToBrandRepo } from './repositories/indices-repo.js';
import type { PlatformBrand } from '@kilat/shared';

/**
 * Resolve the platform brand for the current host (ADR-0017).
 *
 * Order:
 *   1. dev fast path: localhost / 127.0.0.1 / *.local → bootstrap brand
 *   2. hostnameToBrand/{host} (the index doc — O(1) lookup)
 *   3. fallback: scan brands and match domain fields
 *   4. final fallback: bootstrap brand id
 */
const BOOTSTRAP_BRAND_ID = 'kilat';

export async function resolveBrandIdForHost(host: string): Promise<string> {
  const normalised = host.toLowerCase().split(':')[0] ?? host.toLowerCase();

  if (
    normalised === 'localhost' ||
    normalised.startsWith('127.0.0.1') ||
    normalised.endsWith('.local')
  ) {
    return BOOTSTRAP_BRAND_ID;
  }

  const indexed = await hostnameToBrandRepo.get(normalised);
  if (indexed) return indexed.platformBrandId;

  const brands = await platformBrandsRepo.list();
  for (const brand of brands) {
    const d = brand.domains;
    if (
      normalised === d.marketing ||
      normalised === d.dashboard ||
      normalised === d.agent ||
      normalised === d.find ||
      normalised.endsWith('.' + d.customerSubdomainSuffix) ||
      (d.aliases ?? []).includes(normalised)
    ) {
      return brand.id;
    }
  }

  logger.warn('brand-resolver: no brand matched host; using bootstrap', { host: normalised });
  return BOOTSTRAP_BRAND_ID;
}

export async function loadBrand(brandId: string): Promise<PlatformBrand> {
  const brand = await platformBrandsRepo.get(brandId);
  if (!brand) throw new Error(`platformBrands/${brandId} not found`);
  return brand;
}
